import React, { useState, useRef } from 'react';
import { CheckboxGroup, Checkbox, Input } from '@fiverr-private/forms';
import { translate } from '@fiverr-private/i18n-react';
import { Container } from '@fiverr-private/layout_components';

interface FormCheckboxGroupProps {
  name?: string;
  options: string[];
  translationPrefix: string;
  value: string[];
  onChange: (values: string[]) => void;
  withOther?: boolean;
  onOtherChange?: (newVal: string, prevVal: string) => void;
  maxSelected?: number;
  direction?: 'column' | 'row' | 'rowReverse' | 'columnReverse';
  isError?: boolean;
}

const FormCheckboxGroup: React.FC<FormCheckboxGroupProps> = ({
  name: _name,
  options,
  translationPrefix,
  value,
  onChange,
  withOther = false,
  onOtherChange,
  maxSelected,
  direction = 'column',
  isError = false,
}) => {
  const [other, setOther] = useState<string>('');
  const [otherIsChecked, setOtherIsChecked] = useState<boolean>(false);
  const otherInputRef = useRef<HTMLInputElement>(null);

  const selectedCount = value.length + (other.trim() ? 1 : 0);

  const updateOtherCheckedStatus = () => {
    setOtherIsChecked(Boolean(other) || document.activeElement === otherInputRef.current);
  };

  const handleCheckboxChange = (values: string[]) => {
    const filteredValues = values.filter((val) => val !== other);
    onChange(filteredValues);
  };

  const handleOtherClick = () => {
    if (otherInputRef.current) {
      otherInputRef.current.focus();
    }

    if (otherIsChecked) {
      setOther('');
      onOtherChange?.('', other);
      updateOtherCheckedStatus();
    }
  };

  const handleOtherInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setOther(newValue);
    onOtherChange?.(newValue, other);
    updateOtherCheckedStatus();
  };

  return (
    <Container>
      <CheckboxGroup value={value} onChange={handleCheckboxChange} direction={direction} isError={isError}>
        {options.map((key) => (
          <Checkbox
            key={key}
            value={key}
            disabled={Boolean(maxSelected && !value.includes(key) && selectedCount >= maxSelected)}
          >
            {translate(`${translationPrefix}.${key}`)}
          </Checkbox>
        ))}
      </CheckboxGroup>

      {withOther && (
        <Container paddingY="1">
          <Checkbox
            onClick={handleOtherClick}
            value={other}
            checked={otherIsChecked}
            disabled={Boolean(maxSelected && !otherIsChecked && selectedCount - (other.trim() ? 1 : 0) >= maxSelected)}
          >
            <Input
              ref={otherInputRef}
              placeholder="Other"
              value={other}
              onChange={handleOtherInputChange}
              onBlur={updateOtherCheckedStatus}
              disabled={Boolean(
                maxSelected && !otherIsChecked && selectedCount - (other.trim() ? 1 : 0) >= maxSelected
              )}
              height="1.5rem"
            />
          </Checkbox>
        </Container>
      )}
    </Container>
  );
};

export default FormCheckboxGroup;
